import React, { useEffect, useRef } from "react";

/**
 * Project media. Video only downloads and plays while it is actually on screen
 * (preload="none" + IntersectionObserver), so the work chapter costs nothing
 * until it is reached. Falls back to the poster/plate image otherwise.
 */
const ProjectMedia = ({ project, className = "", sizes = "100vw", eager = false }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const play = el.play();
            if (play && typeof play.catch === "function") play.catch(() => {});
          } else {
            el.pause();
          }
        });
      },
      { rootMargin: "120px 0px", threshold: 0.15 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  if (project.video) {
    return (
      <video
        ref={videoRef}
        className={`media-mono h-full w-full object-cover ${className}`}
        poster={project.image}
        muted
        loop
        playsInline
        preload="none"
        aria-label={`${project.title} interface recording`}
      >
        <source src={project.video} type="video/mp4" />
      </video>
    );
  }

  return (
    <img
      src={project.image}
      alt={`${project.title} — ${project.category}`}
      className={`media-mono h-full w-full object-cover ${className}`}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      sizes={sizes}
    />
  );
};

export default ProjectMedia;
